/**
 * groupByLogicalKey is the pure helper every PR-C surface uses to fold a
 * flat amendment-aware row list (compliance, CLINs, LCATs, insight facts)
 * into a current-version map keyed by logicalKey, with the history rows
 * sorted next to it for one-click expansion.
 *
 * Inputs:
 *   - rows: any list of records that carry { logicalKey, isCurrentVersion,
 *     amendmentSequence, amendmentNumber } shape (or undefined-friendly
 *     versions of those fields).
 *   - keyOf: function returning the logical key for a row. Defaults to
 *     reading the `logicalKey` property when not supplied.
 *
 * Outputs:
 *   - currentByKey: Map<key, row> — exactly one row per logical group, the
 *     one marked is_current_version=true. Used as the default render list.
 *   - historyByKey: Map<key, row[]> — every row for the key, sorted by
 *     amendment_sequence DESC then amendment_number rank DESC (current
 *     first). Used by the history drawer / expand mode.
 *   - allKeys: ordered array of distinct logical keys in the order they
 *     appeared in the input. Drives stable iteration order for list views
 *     so toggling history mode doesn't reshuffle the layout.
 *
 * Important: this is a pure derivation. It mutates nothing. Safe to call
 * inside useMemo.
 *
 * Defensive behavior:
 *   - Rows with empty/missing logicalKey are still included, but each gets
 *     its own group keyed by `__no-key-${index}` so they don't collide.
 *     This means legacy data without the merge step's logical_key field
 *     still renders one row per record (no false collapsing).
 *   - When no row in a group is marked is_current_version, the function
 *     picks the row with the highest amendment_sequence as current. This
 *     keeps the UI sane against half-migrated projects.
 */ export function groupByLogicalKey(rows, keyOf) {
    const currentByKey = new Map();
    const historyByKey = new Map();
    const allKeys = [];
    let totalRows = 0;
    for(let i = 0; i < rows.length; i++){
        const row = rows[i];
        totalRows++;
        let key;
        if (keyOf) {
            key = keyOf(row);
        } else {
            key = row.logicalKey ?? undefined;
        }
        if (!key || key.trim() === '') {
            // Defensive: legacy row without a logical key gets its own bucket so
            // it still renders. Caller treats this as "no version chain".
            key = `__no-key-${i}`;
        }
        if (!historyByKey.has(key)) {
            historyByKey.set(key, []);
            allKeys.push(key);
        }
        historyByKey.get(key)?.push(row);
    }
    // Pick the current row for each group + sort the history list.
    for (const key of allKeys){
        const group = historyByKey.get(key);
        if (!group) continue;
        sortRowsLatestFirst(group);
        // Prefer the row marked is_current_version=true. Fall back to the head
        // of the sorted list (highest amendment_sequence) when none is marked
        // — keeps half-migrated projects renderable.
        const explicitCurrent = group.find((r)=>r.isCurrentVersion === true);
        currentByKey.set(key, explicitCurrent ?? group[0]);
    }
    const currentList = allKeys.map((k)=>currentByKey.get(k)).filter((v)=>v != null);
    const multiVersionGroupCount = Array.from(historyByKey.values()).filter((group)=>group.length > 1).length;
    return {
        currentByKey,
        historyByKey,
        allKeys,
        currentList,
        totalRows,
        multiVersionGroupCount
    };
}
/**
 * sortRowsLatestFirst sorts a group of amendment-versioned rows so the
 * latest version is at index 0. Sort key: amendment_sequence DESC, then
 * amendment_number rank DESC (numeric when possible). The same ordering
 * baltigo's mergeShred / mergeCLINs use, so the UI mirrors the merge
 * step's "current version" choice exactly.
 *
 * Mutates the slice in place — caller passes a fresh array.
 */ export function sortRowsLatestFirst(rows) {
    rows.sort((a, b)=>{
        const seqA = a.amendmentSequence ?? 0;
        const seqB = b.amendmentSequence ?? 0;
        if (seqA !== seqB) return seqB - seqA;
        return amendmentRank(b.amendmentNumber) - amendmentRank(a.amendmentNumber);
    });
}
/**
 * amendmentRank parses an amendment number string into a comparable
 * integer for tiebreaker sorting. "" / null is base = 0; "001" / "002"
 * parse directly. Anything unparseable returns the high sentinel so
 * unknown labels still rank as latest.
 *
 * Mirrors the same helper in services/baltigo/pkg/govcon/merge.go so
 * the frontend's ordering matches the merge step exactly.
 */ function amendmentRank(s) {
    if (!s) return 0;
    const trimmed = s.trim();
    if (trimmed === '') return 0;
    const parsed = Number(trimmed);
    if (!Number.isNaN(parsed)) return parsed;
    return 99999;
}
